/**
 * Messages → System (Gideon, Oct 28): what was said about a location or a
 * cleaner but to or from no one — Monday's automation errors, notes, and an
 * automatic reminder that had no one to go to. One row per location (or
 * cleaner), and opening it shows those messages read-only, oldest first like
 * a conversation. An automatic message that did reach someone is in that
 * person's own conversation instead, marked Automatic. Pure.
 */
import { clockAt } from '../shared/zoned.ts'
import type { Communication, Purpose } from './communication.ts'
import { dayLabel, isAuto, oneLine, shortWhen } from './inbox.ts'

/** Automatic messages that belong in System when they reached no one. */
export const systemPurposes = [
  'automation_error',
  'customer_reminder',
  'cleaner_schedule',
] as const satisfies readonly Purpose[]

/** To or from no one, and a note, an automation error or an automatic message — not an app event. */
export function isSystemMessage(m: Pick<Communication, 'channel' | 'purpose' | 'party'>): boolean {
  return (
    m.party.contactId === null &&
    (m.channel === 'internal_note' || (systemPurposes as readonly string[]).includes(m.purpose))
  )
}

export const systemKinds = ['location', 'cleaner'] as const
export interface SystemAbout {
  kind: (typeof systemKinds)[number]
  id: string
}

/** "location-100018" — the address's `about`. */
export const systemKey = (about: SystemAbout) => `${about.kind}-${about.id}`

export function systemAboutOf(key: string | undefined): SystemAbout | null {
  const m = /^(location|cleaner)-(\d{6,})$/.exec(key ?? '')
  return m ? { kind: m[1] as SystemAbout['kind'], id: m[2] ?? '' } : null
}

/** Who a System row is about: a location (on its client) or a cleaner. */
export interface SystemPlace {
  name: string
  /** The client's name, or "Cleaner". */
  context: string
  /** A location's client — "Open location" goes to the location's tab there. */
  clientId: string | null
}

export interface SystemThread extends SystemPlace {
  key: string
  about: SystemAbout
  when: string
  /** "Auto: Couldn’t send the follow-up reminder", or a note's words. */
  preview: string
  /** How many messages it holds. */
  count: number
  at: string
}

/** The row: the place, and its newest message. */
export function systemThreadOf(
  about: SystemAbout,
  place: SystemPlace,
  last: Communication,
  count: number,
  ctx: { now: string; timeZone: string },
): SystemThread {
  return {
    ...place,
    key: systemKey(about),
    about,
    when: shortWhen(last.at, ctx.now, ctx.timeZone),
    preview: `${isAuto(last) ? 'Auto: ' : ''}${oneLine(last)}`,
    count,
    at: last.at,
  }
}

export interface SystemMessage {
  id: string
  kind: 'note' | 'email' | 'event'
  /** An email's subject, an event's title; a note has none. */
  title: string | null
  text: string
  /** "Automatic", or who wrote the note. */
  by: string | null
  time: string
  at: string
}

/** The messages under a heading per day, oldest first — read-only. */
export function systemConversation(
  messages: readonly Communication[],
  ctx: { now: string; timeZone: string },
): { label: string; messages: SystemMessage[] }[] {
  const days: { label: string; messages: SystemMessage[] }[] = []
  const sorted = [...messages].sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id))
  for (const m of sorted) {
    const label = dayLabel(m.at, ctx.now, ctx.timeZone)
    if (days.at(-1)?.label !== label) days.push({ label, messages: [] })
    const note = m.channel === 'internal_note'
    days.at(-1)?.messages.push({
      id: m.id,
      kind: note ? 'note' : m.channel === 'email' ? 'email' : 'event',
      title: note ? null : m.channel === 'email' ? m.subject : m.title,
      text: m.body,
      by: isAuto(m) ? 'Automatic' : (m.author?.name ?? null),
      time: clockAt(m.at, ctx.timeZone),
      at: m.at,
    })
  }
  return days
}
